/**
 * LoginPage - BetterAuth email+password / magic-link login
 */

import { useEffect, useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getToken, betterAuthSignIn, betterAuthMagicLink } from '../../services/auth';
import { sanitizeReturnTo } from '../../utils/navigation';
import { logger } from '../../../utils/logger';
import { HiEnvelope, HiArrowLeft } from 'react-icons/hi2';
import { Input } from '../../components/ui/Input';
import voicebrainLogo from '../../../assets/images/voicebrain-logo.svg';
import loginBackground from '../../../assets/images/login-background.jpg';

const BRAND_NAME = import.meta.env.VITE_BRAND_NAME || 'VoiceBrain';
const ENV_LABEL = import.meta.env.VITE_ENV_LABEL || '';

type LoginMode = 'password' | 'magic-link';

export function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<LoginMode>('magic-link');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  useEffect(() => {
    if (getToken()) {
      const returnTo = sanitizeReturnTo(
        sessionStorage.getItem('auth_return_to') || '/v2/dashboard'
      );
      sessionStorage.removeItem('auth_return_to');
      navigate(returnTo, { replace: true });
    }
  }, [navigate]);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await betterAuthSignIn(email, password);
      if (result.error) {
        setError(result.error);
        return;
      }

      logger.info('[LoginPage] Sign in successful');
      const returnTo = sanitizeReturnTo(
        sessionStorage.getItem('auth_return_to') || '/v2/dashboard'
      );
      sessionStorage.removeItem('auth_return_to');
      navigate(returnTo, { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMagicLink = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await betterAuthMagicLink(email);
      if (result.error) {
        setError(result.error);
        return;
      }
      logger.info('[LoginPage] Magic link sent');
      setMagicLinkSent(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Magic link sent confirmation
  if (magicLinkSent) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBackground})` }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />
        <div className="w-full max-w-sm relative z-10">
          <div
            className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8 text-center"
            role="status"
            aria-live="polite"
          >
            <div className="w-16 h-16 bg-vb-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <HiEnvelope className="w-8 h-8 text-vb-success" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-bold text-vb-text-primary dark:text-vb-text-primary-dark mb-2">
              Check your email
            </h1>
            <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark mb-6">
              We sent a sign-in link to <strong>{email}</strong>. Click it to access {BRAND_NAME}.
            </p>
            <button
              type="button"
              onClick={() => {
                setMagicLinkSent(false);
                setError(null);
              }}
              className="inline-flex items-center gap-2 text-sm text-vb-primary hover:text-vb-primary-hover transition-colors"
            >
              <HiArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${loginBackground})` }}
      role="main"
      aria-label={`${BRAND_NAME} sign in`}
    >
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />

      <div className="w-full max-w-sm relative z-10">
        <div
          className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8"
          role="form"
          aria-labelledby="login-heading"
        >
          {/* Header */}
          <div className="text-center mb-6">
            <img
              src={voicebrainLogo}
              alt={BRAND_NAME}
              className="h-10 mx-auto mb-3 invert dark:invert-0"
            />
            <h1 id="login-heading" className="sr-only">
              {BRAND_NAME} Sign In
            </h1>
            {ENV_LABEL && (
              <p
                className="text-xl text-vb-text-tertiary dark:text-vb-text-tertiary-dark"
                aria-label={`Environment: ${ENV_LABEL}`}
              >
                {ENV_LABEL}
              </p>
            )}
          </div>

          {/* Mode toggle */}
          <div
            className="flex rounded-lg bg-vb-surface-hover/50 dark:bg-vb-surface-hover-dark/50 p-1 mb-6"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'magic-link'}
              className={`flex-1 text-sm py-1.5 rounded-md transition-colors ${
                mode === 'magic-link'
                  ? 'bg-vb-surface dark:bg-vb-surface-dark shadow-sm text-vb-text-primary dark:text-vb-text-primary-dark font-medium'
                  : 'text-vb-text-secondary dark:text-vb-text-secondary-dark'
              }`}
              onClick={() => {
                setMode('magic-link');
                setError(null);
              }}
            >
              Magic Link
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'password'}
              className={`flex-1 text-sm py-1.5 rounded-md transition-colors ${
                mode === 'password'
                  ? 'bg-vb-surface dark:bg-vb-surface-dark shadow-sm text-vb-text-primary dark:text-vb-text-primary-dark font-medium'
                  : 'text-vb-text-secondary dark:text-vb-text-secondary-dark'
              }`}
              onClick={() => {
                setMode('password');
                setError(null);
              }}
            >
              Password
            </button>
          </div>

          {/* Error */}
          {error && (
            <div
              className="mb-4 p-3 rounded-lg bg-vb-danger/10 border border-vb-danger text-sm text-vb-danger"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* Magic Link Form */}
          {mode === 'magic-link' && (
            <form
              onSubmit={handleMagicLink}
              className="space-y-4"
              aria-label="Sign in with magic link"
            >
              <div>
                <label
                  htmlFor="magic-email"
                  className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
                >
                  Email
                </label>
                <Input
                  id="magic-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                  autoFocus
                  aria-required="true"
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                className="w-full px-4 py-2 rounded-lg bg-vb-primary hover:bg-vb-primary-hover text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Sign in'}
              </button>
            </form>
          )}

          {/* Password Form */}
          {mode === 'password' && (
            <form
              onSubmit={handlePasswordSubmit}
              className="space-y-4"
              aria-label="Sign in with password"
            >
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
                >
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                  autoFocus
                  aria-required="true"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
                >
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="current-password"
                  aria-required="true"
                  placeholder="Enter your password"
                />
              </div>

              <button
                type="submit"
                className="w-full px-4 py-2 rounded-lg bg-vb-primary hover:bg-vb-primary-hover text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}

          {/* Links */}
          <div className="mt-6 text-center text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark space-y-2">
            {mode === 'password' && (
              <div>
                <Link
                  to="/v2/forgot-password"
                  className="text-vb-primary hover:text-vb-primary-hover transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>
            )}
            <div>
              Don&apos;t have an account?{' '}
              <Link
                to="/v2/signup"
                className="text-vb-primary hover:text-vb-primary-hover transition-colors"
              >
                Create one
              </Link>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <p className="mt-6 text-center text-xs text-vb-text-tertiary/50 dark:text-vb-text-tertiary-dark/50">
          &copy; {new Date().getFullYear()} {BRAND_NAME}
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
